import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiApplications, ApplicationModel} from '../../../models/api/applicationModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {getApplications, getShouldFetchApplications} from './applicationsSelectors';

/*
 * Action types.
 */

export enum ApplicationsActionsEnum {
  FETCH_APPLICATIONS = '@@data/applications/FETCH_APPLICATIONS',
  FETCH_APPLICATIONS_COMPLETED = '@@data/applications/FETCH_APPLICATIONS_COMPLETED',
  FETCH_APPLICATIONS_FAILED = '@@data/applications/FETCH_APPLICATIONS_FAILED',
  APPLICATION_UPDATED = '@@data/applications/APPLICATION_UPDATED',
  APPLICATION_DELETED = '@@data/applications/APPLICATION_DELETED',
}

/*
 * Types.
 */

export interface FetchApplicationsAction extends ActionBase {
  type: ApplicationsActionsEnum.FETCH_APPLICATIONS;
}

export interface FetchApplicationsCompletedAction extends ActionWithPayload<ReadonlyArray<ApplicationModel>> {
  type: ApplicationsActionsEnum.FETCH_APPLICATIONS_COMPLETED;
}

export interface FetchApplicationsFailedAction extends ActionBase {
  type: ApplicationsActionsEnum.FETCH_APPLICATIONS_FAILED;
}
interface ApplicationUpdatedAction extends ActionWithPayload<ApplicationModel> {
  type: ApplicationsActionsEnum.APPLICATION_UPDATED;
}
interface ApplicationDeletedAction extends ActionWithPayload<number> {
  type: ApplicationsActionsEnum.APPLICATION_DELETED;
}

/*
 * Action creators.
 */

function fetchApplicationsAction(): FetchApplicationsAction {
  return {
    type: ApplicationsActionsEnum.FETCH_APPLICATIONS,
  };
}

function fetchApplicationsActionCompleted(
  applications: ReadonlyArray<ApplicationModel>,
): FetchApplicationsCompletedAction {
  return {
    type: ApplicationsActionsEnum.FETCH_APPLICATIONS_COMPLETED,
    payload: applications,
  };
}

function fetchApplicationsActionFailed(): FetchApplicationsFailedAction {
  return {
    type: ApplicationsActionsEnum.FETCH_APPLICATIONS_FAILED,
  };
}

/*
 * Actions.
 */

export function fetchApplicationsAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const shouldFetchApplications = getShouldFetchApplications(getState());
    if (!shouldFetchApplications) {
      return;
    }

    dispatch(fetchApplicationsAction());

    try {
      const companyUrl = getCompanyUrl(getState());
      const applications = await fetchApiAsync(ApiApplications, `${companyUrl}/application_installations`);
      dispatch(fetchApplicationsActionCompleted(applications));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchApplicationsActionFailed());
    }
  };
}

export function updateApplicationAction(newApplication: ApplicationModel): ThunkAction {
  return (dispatch, getState) => {
    const applications = getApplications(getState());
    const existingApplication = applications[newApplication.id];
    if (existingApplication && existingApplication.updatedAt > newApplication.updatedAt) {
      return;
    }

    const action: ApplicationUpdatedAction = {
      type: ApplicationsActionsEnum.APPLICATION_UPDATED,
      payload: newApplication,
    };

    dispatch(action);
  };
}

export function deleteApplicationAction(applicationId: number): ThunkAction {
  return (dispatch, getState) => {
    const applications = getApplications(getState());
    const existingApplication = applications[applicationId];
    if (!existingApplication) {
      return;
    }

    const action: ApplicationDeletedAction = {
      type: ApplicationsActionsEnum.APPLICATION_DELETED,
      payload: applicationId,
    };

    dispatch(action);
  };
}

/*
 * Union type.
 */

export type ApplicationsAction =
  | FetchApplicationsAction
  | FetchApplicationsCompletedAction
  | FetchApplicationsFailedAction
  | ApplicationUpdatedAction
  | ApplicationDeletedAction;
